import React, { useState, useEffect, useRef } from 'react';
import {
  User, Phone, MapPin, Building2,
  CreditCard, Edit2, Save, X, Loader, AlertCircle, CheckCircle,
  Paperclip, Trash2, FileText, Upload, QrCode, Download, Printer,
} from 'lucide-react';
import QRCode from 'react-qr-code';
import { employeeAPI, establishmentAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth } from './AuthContext';
import { getPrimaryRole } from '../utils/permissions';
import './MyProfile.css';

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const IDENTITY_CATEGORIES = [
  { value: 'aadhaar',  label: 'Aadhaar Card' },
  { value: 'pan',      label: 'PAN Card' },
  { value: 'uan',      label: 'UAN Document' },
  { value: 'esic_ip',  label: 'ESIC IP Card' },
  { value: 'other',    label: 'Other Document' },
];

const Field = ({ label, value, name, editing, onChange, type = 'text' }) => (
  <div className="mp-field">
    <span className="mp-field-label">{label}</span>
    {editing ? (
      <div className="mp-field-input-wrap">
        <input
          className="mp-field-input"
          type={type}
          name={name}
          value={value ?? ''}
          onChange={onChange}
        />
      </div>
    ) : (
      <span className="mp-field-value">{value || <span className="mp-field-empty">—</span>}</span>
    )}
  </div>
);

const Section = ({ icon: Icon, title, children, headerRight }) => (
  <div className="mp-section">
    <div className="mp-section-head">
      <Icon size={16} className="mp-section-icon" />
      <h3 className="mp-section-title">{title}</h3>
      {headerRight && <div className="mp-section-head-right">{headerRight}</div>}
    </div>
    <div className="mp-section-body">{children}</div>
  </div>
);

// ── ClientAdmin: My Organization view ───────────────────────────────
function OrgProfile() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    establishmentAPI.getAll()
      .then(r => {
        const all = r.establishments || [];
        // Prefer the org linked to this user's establishment_id
        const estId = user?.establishment_id;
        const matched = estId
          ? all.find(e => e.id === estId) || all[0]
          : all[0];
        setOrg(matched || null);
      })
      .catch(err => setError(err.message || 'Could not load organization details'))
      .finally(() => setLoading(false));
  }, [user?.establishment_id]);

  if (loading) return (
    <div className="mp-state-center">
      <Loader size={28} className="mp-spinner" />
      <p>Loading organization…</p>
    </div>
  );

  if (error || !org) return (
    <div className="mp-state-center mp-state-warn">
      <AlertCircle size={28} />
      <p>{error || 'No organization record is linked to your account.'}</p>
    </div>
  );

  const initials = (org.name || 'O').slice(0, 2).toUpperCase();

  return (
    <div className="mp-page">
      <div className="mp-hero">
        <div className="mp-avatar">{initials}</div>
        <div className="mp-hero-info">
          <h1 className="mp-hero-name">{org.name}</h1>
          <div className="mp-hero-meta">
            {org.industry_type && <span>{org.industry_type}</span>}
            {org.business_type && <><span className="mp-dot">·</span><span>{org.business_type}</span></>}
            {org.code && <><span className="mp-dot">·</span><span className="mp-badge">{org.code}</span></>}
          </div>
          <div className="mp-hero-id">Organization ID: {org.id}</div>
        </div>
      </div>

      <div className="mp-grid">
        <Section icon={Building2} title="Organization Details">
          <Field label="Name"             value={org.name} />
          <Field label="Code"             value={org.code} />
          <Field label="Industry Type"    value={org.industry_type} />
          <Field label="Business Type"    value={org.business_type} />
          <Field label="Registered Date"  value={org.registered_date} />
          <Field label="Nature of Work"   value={org.nature_of_work} />
        </Section>

        <Section icon={MapPin} title="Address">
          <Field label="Address"          value={org.address} />
          <Field label="City"             value={org.city} />
          <Field label="State"            value={org.state} />
          <Field label="ZIP Code"         value={org.zip_code} />
          <Field label="Country"          value={org.country} />
        </Section>

        <Section icon={Phone} title="Contact">
          <Field label="Main Email"       value={org.main_email} />
          <Field label="Phone"            value={org.phone} />
          <Field label="Website"          value={org.website} />
          <Field label="Manager Email"    value={org.manager_email} />
        </Section>

        <Section icon={CreditCard} title="Statutory Details">
          <Field label="PAN"              value={org.pan} />
          <Field label="GSTIN"            value={org.gstin} />
          <Field label="EPF Code"         value={org.epf_code} />
          <Field label="ESIC Code"        value={org.esic_code} />
          <Field label="PT Number"        value={org.pt_number} />
          <Field label="LIN Number"       value={org.lin_number} />
        </Section>
      </div>
    </div>
  );
}

// ── Member employee profile (original component) ─────────────────────
function EmployeeProfile() {
  const { user } = useAuth();
  const [profile, setProfile]   = useState(null);
  const [draft, setDraft]       = useState(null);
  const [editing, setEditing]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState(null);

  // Attachment state
  const [attachOpen, setAttachOpen]         = useState(false);
  const [attachments, setAttachments]       = useState([]);
  const [attachLoading, setAttachLoading]   = useState(false);
  const [uploading, setUploading]           = useState(false);
  const [attachError, setAttachError]       = useState('');
  const [uploadCategory, setUploadCategory] = useState('aadhaar');
  const fileInputRef = useRef(null);

  useEffect(() => {
    employeeAPI.getMe()
      .then(data => { setProfile(data); setDraft(data); })
      .catch(err => setToast({ type: 'error', msg: err.message || 'Could not load profile' }))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setDraft(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => { setDraft({ ...profile }); setEditing(true); setToast(null); };
  const handleCancel = () => { setDraft({ ...profile }); setEditing(false); setToast(null); };

  const handleSave = async () => {
    setSaving(true);
    setToast(null);
    try {
      await employeeAPI.update(profile.employee_id, {
        section: 'personal',
        first_name:            draft.first_name,
        last_name:             draft.last_name,
        date_of_birth:         draft.date_of_birth,
        gender:                draft.gender,
        blood_group:           draft.blood_group,
        marital_status:        draft.marital_status,
        phone:                 draft.phone,
        mobile:                draft.mobile,
        emergency_contact_no:  draft.emergency_contact_no,
        next_of_kin_name:      draft.next_of_kin_name,
        next_of_kin_relation:  draft.next_of_kin_relation,
        address:               draft.address,
        address_line2:         draft.address_line2,
        city:                  draft.city,
        state:                 draft.state,
        zip_code:              draft.zip_code,
        aadhar:                draft.aadhar,
        pan:                   draft.pan,
        uan:                   draft.uan,
        esic_ip:               draft.esic_ip,
      });
      await employeeAPI.update(profile.employee_id, {
        section:        'banking',
        bank_name:      draft.bank_name,
        account_number: draft.account_number,
        ifsc:           draft.ifsc,
        account_type:   draft.account_type,
      });
      setProfile({ ...profile, ...draft });
      setEditing(false);
      notifySavedAndReload('Profile updated successfully.');
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to save changes.' });
    } finally {
      setSaving(false);
    }
  };

  // --- Attachment handlers ---
  const loadAttachments = async (empId) => {
    setAttachLoading(true);
    setAttachError('');
    try {
      const data = await employeeAPI.getAttachments(empId);
      setAttachments(data.attachments || data || []);
    } catch {
      setAttachError('Could not load attachments.');
    } finally {
      setAttachLoading(false);
    }
  };

  const handleToggleAttach = () => {
    if (!attachOpen) {
      loadAttachments(profile.employee_id);
    }
    setAttachOpen(prev => !prev);
    setAttachError('');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setAttachError('');
    try {
      await employeeAPI.uploadAttachment(file, profile.employee_id, uploadCategory);
      await loadAttachments(profile.employee_id);
    } catch (err) {
      setAttachError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    setAttachError('');
    try {
      await employeeAPI.deleteAttachment(profile.employee_id, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch {
      setAttachError('Could not delete attachment.');
    }
  };

  const initials = profile
    ? `${profile.first_name?.[0] ?? ''}${profile.last_name?.[0] ?? ''}`.toUpperCase()
    : user?.username?.[0]?.toUpperCase() ?? 'U';

  if (loading) return (
    <div className="mp-state-center">
      <Loader size={28} className="mp-spinner" />
      <p>Loading your profile…</p>
    </div>
  );

  if (!profile && !loading) return (
    <div className="mp-state-center mp-state-warn">
      <AlertCircle size={28} />
      <p>No employee record is linked to your account ({user?.email}).<br />
         Please contact your administrator.</p>
    </div>
  );

  const d = editing ? draft : profile;

  return (
    <div className="mp-page">
      {/* Hero card */}
      <div className="mp-hero">
        <div className="mp-avatar">{initials}</div>
        <div className="mp-hero-info">
          <h1 className="mp-hero-name">{profile.first_name} {profile.last_name}</h1>
          <div className="mp-hero-meta">
            {profile.position && <span>{profile.position}</span>}
            {profile.department && <><span className="mp-dot">·</span><span>{profile.department}</span></>}
            {profile.employment_type && <><span className="mp-dot">·</span><span className="mp-badge">{profile.employment_type}</span></>}
          </div>
          {profile.establishment_name && (
            <div className="mp-hero-org">
              <Building2 size={13} /> {profile.establishment_name}
            </div>
          )}
          <div className="mp-hero-id">Employee ID: {profile.employee_id}</div>
        </div>
        <div className="mp-hero-actions">
          {!editing ? (
            <button className="mp-btn-edit" onClick={handleEdit}>
              <Edit2 size={15} /> Edit Profile
            </button>
          ) : (
            <>
              <button className="mp-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? <Loader size={15} className="mp-spinner-sm" /> : <Save size={15} />}
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="mp-btn-cancel" onClick={handleCancel} disabled={saving}>
                <X size={15} /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`mp-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          <span>{toast.msg}</span>
        </div>
      )}

      <div className="mp-grid">
        {/* Personal Information */}
        <Section icon={User} title="Personal Information">
          <Field label="First Name"     value={d.first_name}     name="first_name"     editing={editing} onChange={handleChange} />
          <Field label="Last Name"      value={d.last_name}      name="last_name"      editing={editing} onChange={handleChange} />
          <Field label="Date of Birth"  value={d.date_of_birth}  name="date_of_birth"  editing={editing} onChange={handleChange} type="date" />
          <Field label="Gender"         value={d.gender}         name="gender"         editing={editing} onChange={handleChange} />
          <Field label="Blood Group"    value={d.blood_group}    name="blood_group"    editing={editing} onChange={handleChange} />
          <Field label="Marital Status" value={d.marital_status} name="marital_status" editing={editing} onChange={handleChange} />
        </Section>

        {/* Contact */}
        <Section icon={Phone} title="Contact">
          <Field label="Email"              value={d.email}                name="email"                editing={false} />
          <Field label="Phone"              value={d.phone}                name="phone"                editing={editing} onChange={handleChange} />
          <Field label="Mobile"             value={d.mobile}               name="mobile"               editing={editing} onChange={handleChange} />
          <Field label="Emergency Contact"  value={d.emergency_contact_no} name="emergency_contact_no" editing={editing} onChange={handleChange} />
          <Field label="Next of Kin"        value={d.next_of_kin_name}     name="next_of_kin_name"     editing={editing} onChange={handleChange} />
          <Field label="Relation"           value={d.next_of_kin_relation} name="next_of_kin_relation" editing={editing} onChange={handleChange} />
        </Section>

        {/* Address */}
        <Section icon={MapPin} title="Address">
          <Field label="Address Line 1" value={d.address}       name="address"       editing={editing} onChange={handleChange} />
          <Field label="Address Line 2" value={d.address_line2} name="address_line2" editing={editing} onChange={handleChange} />
          <Field label="City"           value={d.city}          name="city"          editing={editing} onChange={handleChange} />
          <Field label="State"          value={d.state}         name="state"         editing={editing} onChange={handleChange} />
          <Field label="ZIP Code"       value={d.zip_code}      name="zip_code"      editing={editing} onChange={handleChange} />
        </Section>

        {/* Identity Documents — with attachment button in header */}
        <Section
          icon={CreditCard}
          title="Identity Documents"
          headerRight={
            <button
              className={`mp-btn-attach ${attachOpen ? 'active' : ''}`}
              onClick={handleToggleAttach}
              title="Upload / view identity document attachments"
            >
              <Paperclip size={14} />
              {attachments.length > 0 && <span className="mp-attach-badge">{attachments.length}</span>}
            </button>
          }
        >
          <Field label="Aadhaar" value={d.aadhar}  name="aadhar"  editing={editing} onChange={handleChange} />
          <Field label="PAN"     value={d.pan}      name="pan"     editing={editing} onChange={handleChange} />
          <Field label="UAN"     value={d.uan}      name="uan"     editing={editing} onChange={handleChange} />
          <Field label="ESIC IP" value={d.esic_ip} name="esic_ip" editing={editing} onChange={handleChange} />
        </Section>

        {/* Bank Details */}
        <Section icon={Building2} title="Bank Details">
          <Field label="Bank Name"      value={d.bank_name}      name="bank_name"      editing={editing} onChange={handleChange} />
          <Field label="Account Number" value={d.account_number} name="account_number" editing={editing} onChange={handleChange} />
          <Field label="IFSC Code"      value={d.ifsc}           name="ifsc"           editing={editing} onChange={handleChange} />
          <Field label="Account Type"   value={d.account_type}   name="account_type"   editing={editing} onChange={handleChange} />
        </Section>

        {/* Attendance Barcode */}
        {profile.barcode && (
          <Section icon={QrCode} title="Attendance Barcode">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0' }}>
              <div id="mp-qr-content" style={{ background: 'var(--theme-surface)', padding: '12px', borderRadius: '8px', display: 'inline-block' }}>
                <QRCode value={profile.barcode} size={160} />
              </div>
              <code style={{ fontSize: '0.8rem', color: '#4a5568', background: 'var(--theme-surfaceHover)', padding: '0.35rem 0.75rem', borderRadius: '6px', letterSpacing: '0.03em' }}>
                {profile.barcode}
              </code>
              <span style={{ fontSize: '0.7rem', color: '#a0aec0' }}>Show this QR at the scanner to check in/out</span>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                <button
                  type="button"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.8rem', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => {
                    const svg = document.querySelector('#mp-qr-content svg');
                    if (!svg) return;
                    const empName = d.first_name ? `${d.first_name} ${d.last_name || ''}`.trim() : 'Employee';
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const img = new Image();
                    img.onload = () => {
                      canvas.width = 300; canvas.height = 360;
                      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 300, 360);
                      ctx.fillStyle = '#1e293b'; ctx.font = 'bold 16px sans-serif'; ctx.textAlign = 'center';
                      ctx.fillText(empName, 150, 30);
                      ctx.drawImage(img, 50, 50, 200, 200);
                      ctx.fillStyle = '#4a5568'; ctx.font = '11px monospace';
                      ctx.fillText(profile.barcode, 150, 280);
                      if (d.employee_id) { ctx.fillStyle = '#64748b'; ctx.font = '11px sans-serif'; ctx.fillText('ID: ' + d.employee_id, 150, 300); }
                      const link = document.createElement('a');
                      link.download = `barcode-${d.employee_id || 'employee'}.png`;
                      link.href = canvas.toDataURL('image/png');
                      link.click();
                    };
                    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                  }}
                >
                  <Download size={13} /> Download
                </button>
                <button
                  type="button"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.8rem', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => {
                    const svg = document.querySelector('#mp-qr-content svg');
                    if (!svg) return;
                    const empName = d.first_name ? `${d.first_name} ${d.last_name || ''}`.trim() : 'Employee';
                    const svgHtml = new XMLSerializer().serializeToString(svg);
                    const win = window.open('', '_blank', 'width=400,height=500');
                    win.document.write(`<html><head><title>Barcode - ${empName}</title></head><body style="text-align:center;font-family:sans-serif;padding:2rem;">
                      <h2 style="margin:0 0 1rem">${empName}</h2>
                      ${svgHtml}
                      <p style="font-family:monospace;font-size:12px;margin-top:0.75rem">${profile.barcode}</p>
                      ${d.employee_id ? `<p style="font-size:12px;color:#666">ID: ${d.employee_id}</p>` : ''}
                      <script>setTimeout(()=>{window.print();},400);<\/script>
                    </body></html>`);
                    win.document.close();
                  }}
                >
                  <Printer size={13} /> Print
                </button>
              </div>
            </div>
          </Section>
        )}
      </div>

      {/* Attachment Modal */}
      {attachOpen && (
        <div className="adoc-backdrop" onClick={() => setAttachOpen(false)}>
          <div className="adoc-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="adoc-header">
              <span className="adoc-title">Upload Identity Documents</span>
              <button className="adoc-close" onClick={() => setAttachOpen(false)}><X size={16} /></button>
            </div>

            {/* Body */}
            <div className="adoc-body">

              {/* Select + Upload row */}
              <div className="adoc-row">
                <label className="adoc-label">Document Type</label>
                <div className="adoc-controls">
                  <select
                    className="adoc-select"
                    value={uploadCategory}
                    onChange={e => setUploadCategory(e.target.value)}
                    disabled={uploading}
                  >
                    {IDENTITY_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <button
                    className="adoc-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading
                      ? <><Loader size={14} className="mp-spinner-sm" /> Uploading…</>
                      : <><Upload size={14} /> Upload File</>
                    }
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                </div>
                <p className="adoc-hint">Accepted formats: PDF, JPG, PNG, DOC, DOCX</p>
              </div>

              {/* Error */}
              {attachError && (
                <p className="adoc-error"><AlertCircle size={13} /> {attachError}</p>
              )}

              {/* File list */}
              <div className="adoc-list-wrap">
                {attachLoading ? (
                  <div className="adoc-state"><Loader size={20} className="mp-spinner" /><span>Loading…</span></div>
                ) : attachments.length === 0 ? (
                  <div className="adoc-state adoc-empty">
                    <FileText size={32} />
                    <span>No documents uploaded yet</span>
                  </div>
                ) : (
                  attachments.map(att => (
                    <div key={att.id} className="adoc-file">
                      <FileText size={16} className="adoc-file-ico" />
                      <div className="adoc-file-info">
                        <span className="adoc-file-name">{att.original_file_name || att.file_name}</span>
                        <span className="adoc-file-meta">
                          {IDENTITY_CATEGORIES.find(c => c.value === att.category)?.label || att.category}
                          {att.file_size ? ` · ${fmtSize(att.file_size)}` : ''}
                        </span>
                      </div>
                      {att.s3_url && (
                        <a href={att.s3_url} target="_blank" rel="noopener noreferrer" className="adoc-dl">Download</a>
                      )}
                      <button className="adoc-del" onClick={() => handleDeleteAttachment(att.id)} title="Delete">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

// ── MultiClientAdmin: shows all assigned organizations ──────────────
function MultiOrgProfile() {
  const { user } = useAuth();
  const [orgs, setOrgs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    establishmentAPI.getAll()
      .then(r => setOrgs(r.establishments || []))
      .catch(err => setError(err.message || 'Could not load organizations'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="mp-state-center">
      <Loader size={28} className="mp-spinner" />
      <p>Loading organizations…</p>
    </div>
  );

  if (error) return (
    <div className="mp-state-center mp-state-warn">
      <AlertCircle size={28} />
      <p>{error}</p>
    </div>
  );

  const initials = (user?.username || 'M').slice(0, 2).toUpperCase();

  return (
    <div className="mp-page">
      <div className="mp-hero">
        <div className="mp-avatar">{initials}</div>
        <div className="mp-hero-info">
          <h1 className="mp-hero-name">{user?.username || 'Multi-Client Admin'}</h1>
          <div className="mp-hero-meta">
            <span style={{
              padding: '0.2rem 0.65rem', borderRadius: '9999px', fontSize: '0.8rem',
              fontWeight: '600', background: '#ede9fe', color: '#5b21b6',
            }}>MultiClientAdmin</span>
          </div>
          {user?.email && <div className="mp-hero-id">{user.email}</div>}
        </div>
      </div>

      <div className="mp-grid">
        <Section icon={Building2} title={`Assigned Organizations (${orgs.length})`}>
          {orgs.length === 0 ? (
            <div style={{ padding: '1rem 0', color: 'var(--theme-textSecondary, #6b7280)', fontSize: '0.875rem' }}>
              No organizations assigned yet. Contact a SuperAdmin.
            </div>
          ) : (
            orgs.map(org => (
              <div key={org.id} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.75rem 0',
                borderBottom: '1px solid var(--theme-border, #f0f0f0)',
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '8px',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: '700', fontSize: '0.75rem', flexShrink: 0,
                }}>
                  {(org.name || 'O').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: 'var(--theme-text, #111827)', fontSize: '0.9rem' }}>
                    {org.name}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--theme-textSecondary, #6b7280)', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {org.code && <span>{org.code}</span>}
                    {org.industry_type && <><span style={{ color: '#d1d5db' }}>·</span><span>{org.industry_type}</span></>}
                    {org.city && <><span style={{ color: '#d1d5db' }}>·</span><span>{org.city}</span></>}
                  </div>
                </div>
              </div>
            ))
          )}
        </Section>
      </div>
    </div>
  );
}

// ── Default export: routes to correct profile view by role ───────────
export default function MyProfile() {
  const { user } = useAuth();
  const role = getPrimaryRole(user);
  if (role === 'SuperAdmin')        return <OrgProfile />;
  if (role === 'MultiClientAdmin')  return <MultiOrgProfile />;
  if (role === 'ClientAdmin')       return <OrgProfile />;
  return <EmployeeProfile />;
}
